/**
 * Lead Scanner's react-query CACHE surface: the query keys, plus the two
 * imperative cache writes `ScanReview.tsx`'s `submitMutation.onSuccess`
 * performs. No hooks, no `_shared/hooks`, no `web/app-home` — deliberately.
 *
 * Why this is a leaf and not just more of `hooks/useScanApi.ts`: anything
 * `mini-apps/**\/__tests__/**` imports is pulled into `tsc -p
 * tsconfig.tests.json`'s program (the `bun run lint` gate), and
 * `hooks/useScanApi.ts` reaches `web/app-home/src/lib/api.ts` through
 * `_shared/hooks` — which that project can't compile (`import.meta` under a
 * non-ESM `module`, and no `@shared/*` path mapping). Same constraint that
 * put `FetchError`'s shape in the zero-import `_shared/fetch-error-types.ts`
 * leaf, and the same "frontend-safe leaf" pattern as `scan-review-logic.ts`.
 * `hooks/useScanApi.ts` re-exports everything here, so call sites are
 * unaffected by the split.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { Scan } from './scan';

export function scansQueryKey(scannedByEmail: string | null) {
  return ['lead-scanner', 'scans', scannedByEmail] as const;
}

/** The unified Scans page's unfiltered every-rep list (`useAllScansQuery`). */
export function allScansQueryKey() {
  return ['lead-scanner', 'scans', 'all'] as const;
}

export function submitStatusQueryKey(scanId: string) {
  return ['lead-scanner', 'submit-status', scanId] as const;
}

/**
 * Write a server-authoritative `Scan` row straight into EVERY cached scans
 * list, without waiting for that list's own poll or invalidation to land.
 *
 * `ScanReview` never fetches its own `scan` — it receives it as a prop,
 * sourced from whichever scans-list query the surface above it mounted
 * (`useScansQuery` on Home / `ActiveScanPanel`, `useAllScansQuery` on the
 * Scans page). So between "the submit endpoint answered" and "the scans list
 * happens to re-poll", `ScanReview`'s `scan.status` is genuinely behind what
 * the server has already committed.
 *
 * That lag is not cosmetic. `resolveEnrichedInfoState`
 * (`scan-review-logic.ts`) deliberately DISTRUSTS a pending result whenever
 * `scan.status` is already terminal — the carve-out that un-sticks a run
 * whose per-scan poll died. A Retry / "Add note & resend" on an already-
 * `needs_review`/`synced` row walks straight into that carve-out with a
 * merely-stale (not finished) status and re-renders the PREVIOUS run's
 * result as authoritative, seconds after the rep fired a new one.
 *
 * The fix is not a new fetch: `POST /scans/:id/submit` already answers with
 * the freshly-claimed `submitting` row (`api.ts` Step 3 claims atomically
 * before responding), so the correct data is already in hand at
 * `onSuccess` — it was just never put anywhere the `scan` prop reads from.
 *
 * Matched by PREDICATE, not by key: `ScanReview` is reachable from either
 * list, and the Home-scoped one is keyed by an email this helper has no
 * business knowing. Replace-only (never append) — a list that legitimately
 * excludes this row (another rep's scoped list) must stay excluding it.
 */
export function seedScanIntoScansCaches(queryClient: QueryClient, scan: Scan): void {
  queryClient.setQueriesData<Scan[]>(
    { predicate: (query) => query.queryKey[0] === 'lead-scanner' && query.queryKey[1] === 'scans' },
    (scans) => {
      // `undefined` = a query that exists but has never resolved. Returning it
      // unchanged is react-query's "don't set" signal, so this never
      // materializes a cache entry the app hasn't actually fetched.
      if (!scans) return scans;
      if (!scans.some((row) => row.id === scan.id)) return scans;
      return scans.map((row) => (row.id === scan.id ? scan : row));
    },
  );
}

/**
 * Drop the previous execution's submit-status result the moment a Retry /
 * "Add note & resend" fires a new run. The poll key is per-SCAN, not
 * per-execution, so without this the new run inherits the old run's cached
 * terminal entry — and `useSubmitStatusQuery`'s `refetchInterval` returns
 * `false` on terminal data, which silently ends the new run's only observer.
 *
 * `removeQueries` specifically, and it is doing TWO jobs, not one — the
 * second is easy to refactor away by accident, so it is pinned by
 * `__tests__/submit-cache-seeding.test.ts`:
 *
 *  1. It drops the cache entry (the obvious job).
 *  2. It ABORTS the previous execution's in-flight poll at the react-query
 *     level. `QueryCache.remove()` calls `query.destroy()`, which calls
 *     `query.cancel({silent: true})`, which rejects the retryer's thenable;
 *     `createRetryer`'s `resolve` is guarded by `isResolved()`, so when that
 *     old HTTP request finally comes back — after the NEW run's poll has
 *     already cached its pending result — its value is discarded instead of
 *     overwriting the new run's entry under the shared key. A "tidier"
 *     `resetQueries` / `setQueryData(key, undefined)` / `invalidateQueries`
 *     does NOT cancel, and re-opens exactly that stale-clobber window.
 *
 * (The underlying `fetch()` is not aborted — core `apiFetch` owns its own
 * timeout controller and `signal: fetchInit.signal || controller.signal`
 * means threading a query signal through would silently disable
 * `getSubmitStatus`'s deliberate 90s timeout. The orphaned request is
 * wasted work, but it cannot reach the cache.)
 *
 * Called AFTER the fire succeeds, never in `onMutate`: the row already
 * carries the new `actionExecutionId` by then, so the immediate refetch that
 * removing an active query triggers reads the NEW run. Doing it earlier would
 * race the server-side claim and could re-cache the finished PREVIOUS run.
 */
export function resetSubmitStatusForRetry(queryClient: QueryClient, scanId: string): void {
  queryClient.removeQueries({ queryKey: submitStatusQueryKey(scanId) });
}
